export const GTM_CONTAINER_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID

// 初始化 dataLayer

declare global {
	interface Window {
		dataLayer: GTMEvent[]
	}
}

// 定义 GTM 事件的基础类型
interface GTMEvent {
	event: string
	event_name?: string
	event_parameters?: Record<string, unknown> | null
	[key: string]: unknown
}

/**
 * 初始化数据层
 * @description 在客户端环境中初始化window.dataLayer数组
 */
export const initDataLayer = (): void => {
	if (typeof window !== "undefined") {
		window.dataLayer = window.dataLayer || []
	}
}

/**
 * 推送事件到数据层
 * @description 将事件对象推送到window.dataLayer中
 * @param {GTMEvent} event - 要推送的事件对象
 */
export const pushToDataLayer = (event: GTMEvent): void => {
	if (typeof window !== "undefined" && window.dataLayer) {
		window.dataLayer.push(event)
	}
}

/**
 * 页面加载事件
 * @description 推送dle_page_load事件，应在用户信息准备好后调用
 * @param {Object} params - 页面加载事件参数
 * @param {string} [params.pop_user_id] - 用户ID，仅在有有效值时推送
 * @param {string} [params.pop_country_id] - 国家代码，小写格式（如us、ca）
 * @param {boolean} [params.gcs_analytics_storage] - 分析存储同意状态，默认false
 * @param {boolean} [params.gcs_ad_storage] - 广告存储同意状态，默认false
 * @param {boolean} [params.gcs_ad_user_data] - 广告用户数据同意状态，默认false
 * @param {boolean} [params.gcs_ad_personalization] - 广告个性化同意状态，默认false
 */
export const pageLoad = (
	params: {
		pop_user_id?: string
		pop_country_id?: string
		gcs_analytics_storage?: boolean
		gcs_ad_storage?: boolean
		gcs_ad_user_data?: boolean
		gcs_ad_personalization?: boolean
	} = {},
): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping page load event.")
		return
	}

	// 构建event_parameters对象
	const eventParameters: Record<string, unknown> = {}

	// pop_user_id仅在有有效值时添加
	if (params.pop_user_id && params.pop_user_id.trim() !== "") {
		eventParameters.pop_user_id = params.pop_user_id
	}

	// 其他参数，如果未提供则默认为false
	eventParameters.pop_country_id = params.pop_country_id || ""
	eventParameters.gcs_analytics_storage = params.gcs_analytics_storage ?? false
	eventParameters.gcs_ad_storage = params.gcs_ad_storage ?? false
	eventParameters.gcs_ad_user_data = params.gcs_ad_user_data ?? false
	eventParameters.gcs_ad_personalization =
		params.gcs_ad_personalization ?? false

	pushToDataLayer({
		event: "dle_page_load",
		event_parameters: eventParameters,
	})
}

/**
 * 隐私同意更新事件
 * @description 推送dle_consent_update事件，用于更新用户的隐私同意状态
 * @param {Object} params - 隐私同意参数
 * @param {boolean} params.gcs_analytics_storage - 分析存储同意状态
 * @param {boolean} params.gcs_ad_storage - 广告存储同意状态
 * @param {boolean} params.gcs_ad_user_data - 广告用户数据同意状态
 * @param {boolean} params.gcs_ad_personalization - 广告个性化同意状态
 */
export const consentUpdate = (params: {
	gcs_analytics_storage: boolean
	gcs_ad_storage: boolean
	gcs_ad_user_data: boolean
	gcs_ad_personalization: boolean
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping consent update event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_consent_update",
		event_parameters: {
			gcs_analytics_storage: params.gcs_analytics_storage,
			gcs_ad_storage: params.gcs_ad_storage,
			gcs_ad_user_data: params.gcs_ad_user_data,
			gcs_ad_personalization: params.gcs_ad_personalization,
		},
	})
}

/**
 * 注册事件
 * @description 推送dle_login_signup事件，用于追踪用户注册行为
 * @param {Object} params - 注册事件参数
 * @param {string | undefined} params.pop_user_id - 注册成功后回传加密用户ID，可为非空字符串或undefined
 * @param {string} params.status - 状态参数，成功时回传success，失败时回传failed
 */
export const trackSignUp = (params: {
	pop_user_id: string | undefined
	status: "success" | "failed"
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping sign up event.")
		return
	}

	// 构建event_parameters对象
	const eventParameters: Record<string, unknown> = {
		status: params.status,
	}

	// pop_user_id仅在有有效值时添加
	if (params.pop_user_id && params.pop_user_id.trim() !== "") {
		eventParameters.pop_user_id = params.pop_user_id
	}

	pushToDataLayer({
		event: "dle_login_signup",
		event_name: "sign_up",
		event_parameters: eventParameters,
	})
}

/**
 * 登录事件
 * @description 推送dle_login_signup事件，用于追踪用户登录行为
 * @param {Object} params - 登录事件参数
 * @param {string | undefined} params.pop_user_id - 登录成功后回传加密用户ID，可为非空字符串或undefined
 * @param {string} params.status - 状态参数，成功时回传success，失败时回传failed
 */
export const trackLogin = (params: {
	pop_user_id: string | undefined
	status: "success" | "failed"
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping login event.")
		return
	}

	// 构建event_parameters对象
	const eventParameters: Record<string, unknown> = {
		status: params.status,
	}

	// pop_user_id仅在有有效值时添加
	if (params.pop_user_id && params.pop_user_id.trim() !== "") {
		eventParameters.pop_user_id = params.pop_user_id
	}

	pushToDataLayer({
		event: "dle_login_signup",
		event_name: "login",
		event_parameters: eventParameters,
	})
}

/**
 * 下载类型事件
 * @description 推送dle_navigation事件，用于追踪应用商店下载类型
 * @param {Object} params - 下载类型事件参数
 * @param {string} params.type - 下载应用商店类型(google_play、apple_store)
 */
export const trackDownloadType = (params: {
	type: "google_play" | "apple_store"
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping download type event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_navigation",
		event_name: "download_type",
		event_parameters: {
			type: params.type,
		},
	})
}

/**
 * 促销Banner曝光事件
 * @description 推送EC事件，用于追踪促销Banner曝光
 * @param {Object} params - 促销Banner曝光事件参数
 * @param {string} params.promotion_id - 传入跳转目的地url
 * @param {string} params.promotion_name - 传入内部标题
 * @param {string} params.creative_name - 传入图片/视频url
 * @param {string} params.creative_slot - 广告位置
 * @param {string} params.type - 促销banner类型(link、product、collection)
 * @param {Array} params.items - 商品信息数组，每个商品必须包含item_id
 */
export const trackViewPromotion = (params: {
	promotion_id: string
	promotion_name: string
	creative_name: string
	creative_slot?: string
	type: "link" | "product" | "collection"
	items?: Array<{
		item_id: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping view promotion event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_view_promotion",
		event_name: "view_promotion",
		event_parameters: {
			promotion_id: params.promotion_id,
			promotion_name: params.promotion_name,
			creative_name: params.creative_name,
			creative_slot: params.creative_slot,
			type: params.type,
			items: params.items,
		},
	})
}

/**
 * 点击促销Banner事件
 * @description 推送select_promotion事件，用于追踪用户点击促销Banner的行为
 * @param {Object} params - 促销点击参数
 * @param {string} params.promotion_id - 促销ID（传入跳转目的地url）
 * @param {string} params.promotion_name - 促销名称（传入内部标题）
 * @param {string} params.creative_name - 创意名称（传入图片/视频url）
 * @param {string} [params.creative_slot] - 广告位置，可选
 * @param {'link' | 'product' | 'collection'} params.type - 促销banner类型
 * @param {Array} [params.items] - 商品信息数组，可选
 */
export const trackSelectPromotion = (params: {
	promotion_id: string
	promotion_name: string
	creative_name: string
	creative_slot?: string
	type: "link" | "product" | "collection"
	items?: Array<{
		item_id: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping select promotion event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_select_promotion",
		event_name: "select_promotion",
		event_parameters: {
			promotion_id: params.promotion_id,
			promotion_name: params.promotion_name,
			creative_name: params.creative_name,
			creative_slot: params.creative_slot,
			type: params.type,
			items: params.items,
		},
	})
}

/**
 * 商品详情页曝光事件
 * @description 推送view_item事件，用于追踪商品详情页曝光行为
 * @param {Object} params - 商品查看参数
 * @param {string} params.currency - 货币代码（如USD、EUR），必填
 * @param {number} params.value - 商品单价，必填
 * @param {Array} params.items - 商品信息数组，必填
 * @param {string} params.items[].item_id - 商品ID（SPU ID/EXT ID），必填
 * @param {string} params.items[].item_sku - SKU ID，必填
 * @param {string} params.items[].item_name - 商品名称，必填
 * @param {string} params.items[].affiliation - 销售点（AppOnly、PCOnly、all），必填
 * @param {string} params.items[].pop_channel - 销售渠道（shop、secret、store），必填
 * @param {string} params.items[].item_category - IP的ID，可选
 * @param {string} params.items[].item_category2 - 品类的ID值，可选
 * @param {number} params.items[].price - 商品单价，必填
 * @param {string} params.items[].currency - 货币代码，必填
 */
export const trackViewItem = (params: {
	currency: string
	value: number
	items: Array<{
		item_id: string
		item_sku: string
		item_name: string
		affiliation: string
		pop_channel: string
		item_category?: string
		item_category2?: string
		price: number
		currency: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping view item event.")
		return
	}

	pushToDataLayer({
		event: "dle_view_item",
		event_name: "view_item",
		event_parameters: {
			currency: params.currency,
			value: params.value,
			items: params.items,
		},
	})
}

/**
 * 添加到收藏夹事件
 * @description 推送add_to_wishlist事件，用于追踪用户点击收藏按钮的行为
 * @param {Object} params - 收藏参数
 * @param {string} params.currency - 货币代码（如USD、EUR），必填
 * @param {number} params.value - 商品单价，必填
 * @param {Array} params.items - 商品信息数组，必填
 * @param {string} params.items[].item_id - 商品ID（SPU ID/EXT ID），必填
 * @param {string} params.items[].item_sku - SKU ID，必填
 * @param {string} params.items[].item_name - 商品名称，必填
 * @param {string} params.items[].affiliation - 销售点（AppOnly、PCOnly、all），必填
 * @param {string} params.items[].pop_channel - 销售渠道（shop、secret、store），必填
 * @param {string} [params.items[].item_category] - IP的ID，可选
 * @param {string} [params.items[].item_category2] - 品类的ID值，可选
 * @param {number} params.items[].price - 商品单价，必填
 * @param {string} params.items[].currency - 货币代码，必填
 */
export const trackAddToWishlist = (params: {
	currency: string
	value: number
	items: Array<{
		item_id: string
		item_sku: string
		item_name: string
		affiliation: string
		pop_channel: string
		item_category?: string
		item_category2?: string
		price: number
		currency: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping add to wishlist event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_add_to_wishlist",
		event_name: "add_to_wishlist",
		event_parameters: {
			currency: params.currency,
			value: params.value,
			items: params.items,
		},
	})
}

/**
 * 从收藏夹移除事件
 * @description 推送remove_from_wishlist事件，用于追踪用户取消收藏的行为
 * @param {Object} params - 取消收藏参数
 * @param {string} params.item_id - 商品ID，必填
 * @param {string} params.item_name - 商品名称，必填
 */
export const trackRemoveFromWishlist = (params: {
	item_id: string
	item_name: string
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping remove from wishlist event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_remove_from_wishlist",
		event_name: "remove_from_wishlist",
		event_parameters: {
			item_id: params.item_id,
			item_name: params.item_name,
		},
	})
}

/**
 * 添加到购物车事件
 * @description 推送add_to_cart事件，用于追踪用户添加商品到购物车的行为
 * @param {Object} params - 添加到购物车参数
 * @param {string} params.currency - 货币代码（如USD、EUR），必填
 * @param {number} params.value - 加车总价（数量×单价），必填
 * @param {Array} params.items - 商品信息数组，必填
 * @param {string} params.items[].item_id - 商品ID（SPU ID/EXT ID），必填
 * @param {string} params.items[].item_sku - SKU ID，必填
 * @param {string} params.items[].item_name - 商品名称，必填
 * @param {string} params.items[].affiliation - 销售点（AppOnly、PCOnly、all），必填
 * @param {string} params.items[].pop_channel - 销售渠道（shop、secret、store），必填
 * @param {string} [params.items[].item_category] - IP的ID，可选
 * @param {string} [params.items[].item_category2] - 品类的ID值，可选
 * @param {string} params.items[].price - 商品单价，必填
 * @param {number} params.items[].quantity - 加车数量，必填
 * @param {string} params.items[].currency - 货币代码，必填
 */
export const trackAddToCart = (params: {
	currency: string
	value: number
	items: Array<{
		item_id: string
		item_sku: string
		item_name: string
		affiliation: string
		pop_channel: string
		item_category?: string
		item_category2?: string
		price: number
		quantity: number
		currency: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping add to cart event.")
		return
	}

	pushToDataLayer({
		event: "dle_add_to_cart",
		event_name: "add_to_cart",
		event_parameters: {
			currency: params.currency,
			value: params.value,
			items: params.items,
		},
	})
}

/**
 * 购物车页面曝光事件
 * @description 推送view_cart事件，用于追踪购物车页面的曝光，定向曝光当前Tab前五个商品
 * @param {Object} params - 购物车页面曝光参数
 * @param {string} params.currency - 货币代码（如USD、EUR），必填
 * @param {number} params.value - 购物车总价（数量×单价加总），必填
 * @param {Array} params.items - 商品信息数组，必填（前五个商品）
 * @param {string} params.items[].item_id - 商品ID（SPU ID/EXT ID），必填
 * @param {string} params.items[].item_sku - SKU ID，必填
 * @param {string} params.items[].item_name - 商品名称，必填
 * @param {string} params.items[].affiliation - 销售点（AppOnly、PCOnly、all），必填
 * @param {string} params.items[].pop_channel - 销售渠道（shop、secret、store），必填
 * @param {string} [params.items[].item_category] - IP的ID，可选
 * @param {string} [params.items[].item_category2] - 品类的ID值，可选
 * @param {number} params.items[].price - 商品单价，必填
 * @param {number} params.items[].quantity - 车内每种商品的数量，必填
 * @param {string} params.items[].currency - 货币代码，必填
 */
export const trackViewCart = (params: {
	currency: string
	value: number
	items: Array<{
		item_id: string
		item_sku: string
		item_name: string
		affiliation: string
		pop_channel: string
		item_category?: string
		item_category2?: string
		price: number
		quantity: number
		currency: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping view cart event.")
		return
	}

	pushToDataLayer({
		event: "dle_view_cart",
		event_name: "view_cart",
		event_parameters: {
			currency: params.currency,
			value: params.value,
			items: params.items,
		},
	})
}

/**
 * 开始结账事件
 * @description 推送begin_checkout事件，用于追踪用户点击结算按钮并跳转至结账页的行为
 * @param {Object} params - 开始结账参数
 * @param {string} params.currency - 货币代码（如USD、EUR），必填
 * @param {number} params.value - 结账时选中商品总价（数量×单价加总），必填
 * @param {Array} params.items - 商品信息数组，必填
 * @param {string} params.items[].item_id - 商品ID（SPU ID/EXT ID），必填
 * @param {string} params.items[].item_sku - SKU ID，必填
 * @param {string} params.items[].item_name - 商品名称，必填
 * @param {string} params.items[].affiliation - 销售点（AppOnly、PCOnly、all），必填
 * @param {string} params.items[].pop_channel - 销售渠道（shop、secret、store），必填
 * @param {string} [params.items[].item_category] - IP的ID，可选
 * @param {string} [params.items[].item_category2] - 品类的ID值，可选
 * @param {number} params.items[].price - 商品单价，必填
 * @param {number} params.items[].quantity - 结账时选中每种商品的数量，必填
 * @param {string} params.items[].currency - 货币代码，必填
 */
export const trackBeginCheckout = (params: {
	currency: string
	value: number
	items: Array<{
		item_id: string
		item_sku: string
		item_name: string
		affiliation: string
		pop_channel: string
		item_category?: string
		item_category2?: string
		price: number
		quantity: number
		currency: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping begin checkout event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_begin_checkout",
		event_name: "begin_checkout",
		event_parameters: {
			currency: params.currency,
			value: params.value,
			items: params.items,
		},
	})
}

/**
 * 继续付款事件
 * @description 推送proceed_to_pay事件，用于追踪用户点击"继续付款"按钮并发生页面跳转的行为
 */
export const trackProceedToPay = (): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn(
			"GTM container ID not available. Skipping proceed to pay event.",
		)
		return
	}

	pushToDataLayer({
		event: "dle_proceed_to_pay",
		event_name: "proceed_to_pay",
		event_parameters: null,
	})
}

/**
 * 订阅事件
 * @description 推送subscription事件，用于追踪用户点击底部导航栏中"Sign Me Up"按钮的行为
 * @param {Object} params - 订阅参数
 * @param {string} params.status - 订阅状态（success、failed），必填
 */
export const trackSubscription = (params: {
	status: "success" | "failed"
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping subscription event.")
		return
	}

	pushToDataLayer({
		event: "dle_navigation",
		event_name: "subscription",
		event_parameters: {
			status: params.status,
		},
	})
}

/**
 * 搜索事件
 * @description 推送search事件，用于追踪用户输入完搜索词后点击放大镜按钮的行为
 * @param {Object} params - 搜索参数
 * @param {string} params.search_term - 用户搜索词，必填
 */
export const trackSearch = (params: { search_term: string }): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping search event.")
		return
	}

	pushToDataLayer({
		event: "dle_navigation",
		event_name: "search",
		event_parameters: {
			search_term: params.search_term,
		},
	})
}

/**
 * 购买完成事件
 * @description 推送purchase事件，用于追踪用户完成购买的行为
 * @param {Object} params - 购买完成参数
 * @param {string} params.transaction_id - 订单ID，必填
 * @param {string} params.currency - 货币代码，必填
 * @param {number} params.value - 订单总金额（包含运费和税费，减去折扣），必填
 * @param {number} [params.tax] - 税费，可选
 * @param {number} [params.shipping] - 运费，可选
 * @param {string} [params.coupon] - 优惠券名称，可选
 * @param {Array} params.items - 商品信息数组，必填
 * @param {string} params.items[].item_id - 商品ID（SPU ID/EXT ID），必填
 * @param {string} params.items[].item_sku - SKU ID，必填
 * @param {string} params.items[].item_name - 商品名称，必填
 * @param {string} params.items[].affiliation - 销售点（AppOnly、PCOnly、all），必填
 * @param {string} params.items[].pop_channel - 销售渠道（shop、secret、store），必填
 * @param {string} [params.items[].coupon] - 优惠券，可选
 * @param {number} [params.items[].discount] - 折扣率，可选
 * @param {string} [params.items[].item_category] - IP的ID，可选
 * @param {string} [params.items[].item_category2] - 品类的ID值，可选
 * @param {number} params.items[].price - 商品单价，必填
 * @param {number} params.items[].quantity - 商品数量，必填
 * @param {string} params.items[].currency - 货币代码，必填
 */
export const trackPurchase = (params: {
	transaction_id: string
	currency: string
	value: number
	tax?: number
	shipping?: number
	coupon?: string
	items: Array<{
		item_id: string
		item_sku: string
		item_name: string
		affiliation: string
		pop_channel: string
		coupon?: string
		discount?: number
		item_category?: string
		item_category2?: string
		price: number
		quantity: number
		currency: string
	}>
}): void => {
	if (!GTM_CONTAINER_ID) {
		console.warn("GTM container ID not available. Skipping purchase event.")
		return
	}

	pushToDataLayer({
		event: "dle_purchase",
		event_name: "purchase",
		event_parameters: {
			transaction_id: params.transaction_id,
			currency: params.currency,
			value: params.value,
			tax: params.tax,
			shipping: params.shipping,
			coupon: params.coupon,
			items: params.items,
		},
	})
}

// --- GA4 Ecommerce Event Interface (保留用于类型定义) ---

/**
 * 商品信息接口
 * @description 定义电商事件中商品对象的结构
 */
export interface Item {
	item_id: string
	item_name: string
	item_type: string // 固定为 "store"
	// 根据 PRD，其他字段如 price, quantity, currency 等在items数组外部
}
